import { useEffect, useState } from 'react';

/**
 * Hook that fetches and parses available configuration options from WASM module
 * Provides layouts, algorithms, and heuristics discovered from Layli
 * @returns {Object} { options, loading, error }
 *   - options: { layouts: string[], algorithms: string[], heuristics: string[] }
 *   - loading: boolean indicating if WASM module is still initializing
 *   - error: null or error message if parsing failed
 */
const useConfigOptions = () => {
  const [options, setOptions] = useState({
    layouts: [],
    algorithms: [],
    heuristics: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      // Parse the JSON from the WASM module
      const configJson = window.layliConfigOptions;
      
      if (!configJson) {
        throw new Error('WASM module not initialized');
      }

      const parsed = JSON.parse(configJson);
      
      // Validate that we have the expected structure
      if (!parsed.layouts || !parsed.algorithms || !parsed.heuristics) {
        throw new Error('Invalid config structure: missing layouts, algorithms, or heuristics');
      }

      setOptions(parsed);
      setError(null);
    } catch (err) {
      const errorMsg = err instanceof SyntaxError 
        ? `Failed to parse config options: ${err.message}`
        : err.message;
      
      setError(errorMsg);
      console.error('Config options error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { options, loading, error };
};

export default useConfigOptions;
